// ─────────────────────────────────────────────────────────────────────────────
//  leagues.js  —  Torch App · League Management
//
//  Per-league balance architecture:
//    leagues/{leagueId}/members/{userId} → {
//      username, joinedAt,
//      balance,   ← per-league balance (independent per league)
//      wins, losses, streak
//    }
//    users/{userId}.balance → global balance (payday + global leaderboard)
//    users/{userId}.leagueIds → [leagueId, ...]
// ─────────────────────────────────────────────────────────────────────────────

import {
  getFirestore,
  collection, doc,
  addDoc, setDoc, getDoc, getDocs, deleteDoc, updateDoc,
  query, where, orderBy, limit,
  serverTimestamp, increment, arrayUnion, arrayRemove, writeBatch, onSnapshot,
  runTransaction,
} from "firebase/firestore";
import app from "./firebase";
import { getFunctions, httpsCallable } from "firebase/functions";

const db = getFirestore(app);
const fns = getFunctions(app);
const DEFAULT_BALANCE = 1000;

// ── Payday defaults (commissioner can override per-league) ────────────────────
export const PAYDAY_DEFAULTS = {
  paydayEnabled:   true,
  paydayAmount:    200,
  paydayDay:       5,          // 0=Sun … 5=Fri … 6=Sat
  paydayFrequency: "weekly",   // "weekly" | "biweekly" | "monthly"
};

function generateCode(len = 6) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

// REJOIN-KEEPS-RECORD (fix 3.4, 2026-09-28): leaving used to delete the member
// doc outright, and rejoining created a fresh one at startingBalance, 0-0. A
// member who went broke could leave and rejoin for a full bankroll; a pick'em
// member who left lost every win. Leaving now saves the season record to
// leagues/{id}/formerMembers/{uid}, and a rejoin in the SAME season restores
// it. The season number is previousSeasons.length + 1, so once the
// commissioner starts a new season the old record no longer applies and the
// rejoin starts fresh like everyone else.
function leagueSeasonNumber(league) {
  return (Array.isArray(league?.previousSeasons) ? league.previousSeasons.length : 0) + 1;
}

// ─── CREATE LEAGUE ────────────────────────────────────────────────────────────
export async function createLeague(userId, username, name, maxMembers = 20, startingBalance = 1000, endDate = null, isPublic = false, paydaySettings = {}, eventSport = null, startDate = null) {
  if (!userId || !name.trim()) throw new Error("Missing required fields");
  const code    = generateCode();
  // NOTE: `|| DEFAULT_BALANCE` would coerce a deliberate 0 back to 1000, because
  // 0 is falsy. Non-wager leagues (pickem/survivor) pass 0 on purpose — they have
  // no point ledger — so distinguish "not provided" from "explicitly zero".
  const parsedBal = parseInt(startingBalance);
  const balance = Number.isFinite(parsedBal) ? Math.max(0, parsedBal) : DEFAULT_BALANCE;
  const leagueId = doc(collection(db, "leagues")).id; // pre-allocate ID

  // Write 1: league doc
  try {
    // Merge caller's payday settings over defaults
    const pd = { ...PAYDAY_DEFAULTS, ...paydaySettings };
    await setDoc(doc(db, "leagues", leagueId), {
      name:             name.trim(),
      code,
      commissionerId:   userId,
      commissionerName: username || "Commissioner",
      maxMembers:       parseInt(maxMembers) || 20,
      memberCount:      1,
      memberIds:        [userId],
      startingBalance:  balance,
      endDate:          endDate || null,
      isPublic:         isPublic === true,
      eventSport:       eventSport || null,
      startDate:        startDate || null,
      // Payday settings — commissioner can change these after creation
      paydayEnabled:    pd.paydayEnabled,
      paydayAmount:     pd.paydayAmount,
      paydayDay:        pd.paydayDay,
      paydayFrequency:  pd.paydayFrequency,
      // Seed to now so the first payday only fires on the NEXT scheduled cycle,
      // not immediately on league creation.
      lastLeaguePayday: serverTimestamp(),
      createdAt:        serverTimestamp(),
    });
    console.log("[createLeague] Step 1 OK — league doc written");
  } catch (e) {
    console.error("[createLeague] Step 1 FAILED — writing leagues/" + leagueId, e.code, e.message);
    throw new Error("Step 1 failed (" + e.code + "): " + e.message);
  }

  // Write 2: member doc
  try {
    await setDoc(doc(db, "leagues", leagueId, "members", userId), {
      username: username || "Commissioner",
      joinedAt: serverTimestamp(),
      balance,
      wins: 0, losses: 0, streak: 0,
    });
    console.log("[createLeague] Step 2 OK — member doc written");
  } catch (e) {
    console.error("[createLeague] Step 2 FAILED — writing leagues/" + leagueId + "/members/" + userId, e.code, e.message);
    throw new Error("Step 2 failed (" + e.code + "): " + e.message);
  }

  // Write 3: add leagueId to user doc
  try {
    await setDoc(doc(db, "users", userId), { leagueIds: arrayUnion(leagueId) }, { merge: true });
    console.log("[createLeague] Step 3 OK — user leagueIds updated");
  } catch (e) {
    console.error("[createLeague] Step 3 FAILED — writing users/" + userId, e.code, e.message);
    // Non-fatal — league was already created, user can still use it
    // addLeagueToUser will be called separately and can retry
    console.warn("[createLeague] League created but leagueIds not updated — will retry via addLeagueToUser");
  }

  return { id: leagueId, code };
}

// ─── JOIN LEAGUE BY CODE ──────────────────────────────────────────────────────
// M-1 FIX: The capacity check and the member write are now atomic. Previously
// this read memberCount, checked `>= maxMembers`, then wrote the member doc and
// increment(1) in separate calls -- two members joining a near-full league at
// the same time could both pass the check and both commit, pushing the league
// over maxMembers and drifting memberCount/memberIds from the members
// subcollection. The code lookup (a query) can't run inside a transaction, so we
// resolve the leagueId outside, then re-read the league doc INSIDE the txn and
// re-validate capacity + existing-membership against fresh data before writing.
export async function joinLeagueByCode(userId, username, code) {
  // SERVER-OWNED-BALANCES (fix 4.1 step 2, 2026-09-28): joining creates your
  // member doc with a balance, so it runs in joinLeagueCallable (same checks:
  // ended season, already a member, capacity, same-season record restore).
  // It also adds the league to users/{uid}.leagueIds.
  if (!userId || !String(code || "").trim()) throw new Error("Missing required fields");
  try {
    const res = await httpsCallable(fns, "joinLeagueCallable")({ code: String(code).trim(), username });
    return { id: res.data.id, name: res.data.name };
  } catch (e) {
    throw new Error(e?.message && !/^internal$/i.test(e.message) ? e.message : "Couldn't join that league. Try again.");
  }
}

// ─── LOAD MY LEAGUES ─────────────────────────────────────────────────────────
export async function loadMyLeagues(userId) {
  if (!userId) return [];
  const userSnap  = await getDoc(doc(db, "users", userId));
  const leagueIds = userSnap.data()?.leagueIds || [];
  if (leagueIds.length === 0) return [];

  const leagues = await Promise.all(
    leagueIds.map(async (leagueId) => {
      try {
        const lSnap = await getDoc(doc(db, "leagues", leagueId));
        if (!lSnap.exists()) return null;
        const data = lSnap.data();

        const memberSnap = await getDoc(doc(db, "leagues", leagueId, "members", userId));
        const memberData = memberSnap.data() || {};


        let userRank = null;
        let isEnded = data.status === "ended";
        let winner = data.champion || null;

        // Also check endDate for backward compat (leagues ended before status field existed)
        if (!isEnded && data.endDate) {
          const end = data.endDate.toDate ? data.endDate.toDate() : new Date(data.endDate);
          isEnded = end < new Date();
        }

        if (isEnded && !winner) {
          try {
            // Mode-aware: pickem/survivor rank on wins, not balance (all 0).
            const standings = await loadLeagueStandings(leagueId, data.scoringMode || "wager");
            userRank = standings.findIndex(s => s.id === userId) + 1;
            if (standings.length > 0) winner = standings[0];
          } catch {}
        } else if (isEnded) {
          try {
            const standings = await loadLeagueStandings(leagueId, data.scoringMode || "wager");
            userRank = standings.findIndex(s => s.id === userId) + 1;
          } catch {}
        }

        return {
          id:               leagueId,
          name:             data.name,
          code:             data.code,
          memberCount:      data.memberCount,
          maxMembers:       data.maxMembers,
          startingBalance:  data.startingBalance ?? DEFAULT_BALANCE,
          endDate:          data.endDate || null,
          commissionerId:   data.commissionerId,
          commissionerName: data.commissionerName,
          isCommissioner:   data.commissionerId === userId,
          userRank:         userRank || null,
          balance:          memberData.balance ?? data.startingBalance ?? DEFAULT_BALANCE,
          // PROJECTION-WL: the Leagues page card (W–L chip) and the Den masthead
          // "Wins" cell read `wins`/`losses` off these objects. They were never
          // projected, so both rendered nothing/0 for every user since launch —
          // same failure class as the pickPrivacy omission (Aug 29, 2026).
          wins:             memberData.wins   ?? 0,
          losses:           memberData.losses ?? 0,
          status:           data.status || (isEnded ? "ended" : "active"),
          champion:         winner,
          previousSeasons:  data.previousSeasons || [],
          // Payday settings
          paydayEnabled:    data.paydayEnabled  ?? PAYDAY_DEFAULTS.paydayEnabled,
          paydayAmount:     data.paydayAmount   ?? PAYDAY_DEFAULTS.paydayAmount,
          paydayDay:        data.paydayDay      ?? PAYDAY_DEFAULTS.paydayDay,
          paydayFrequency:  data.paydayFrequency ?? PAYDAY_DEFAULTS.paydayFrequency,
          lastLeaguePayday: data.lastLeaguePayday ?? null,
          eventSport:       data.eventSport ?? null,
          sportFilter:      data.sportFilter ?? data.eventSport ?? null,
          startDate:        data.startDate ?? null,
          motto:            data.motto ?? null,
          prize:            data.prize ?? null,
          announcement:     data.announcement ?? null,
          announcementBy:   data.announcementBy ?? null,
          announcementAt:   data.announcementAt ?? null,
          templateId:       data.templateId ?? null,
          isPublic:         data.isPublic ?? false,
          // ── Pick requirements + sport/bet-type restrictions ──
          // These are written by the create-league wizard and read by the
          // League Rules card (and by Cloud Functions enforcing penalties).
          // Without surfacing them here, the client can't render rules even
          // when Firestore has them — the LeagueRules card silently shows
          // only Starting + Payday because pickRequirements arrives undefined.
          pickRequirements: data.pickRequirements ?? null,
          allowedSports:    data.allowedSports    ?? null,
          allowedBetTypes:  data.allowedBetTypes  ?? null,
          timeFrameType:    data.timeFrameType    ?? null,
          // Scoring format: "wager" (default, Vegas odds) | "pickem" | "survivor".
          // Legacy leagues created before this field default to "wager" so the
          // existing balance/odds experience is unchanged for them.
          scoringMode:      data.scoringMode      ?? "wager",
          // Pick privacy: "until_start" hides members' picks from each other
          // until kickoff. EVERY client gate (Den, Picks tab, insights,
          // profiles, Pulse mirror) reads this off THESE objects — omitting
          // it here made the entire feature silently inert in production
          // (Aug 29, 2026, user-reported). This projection is hand-built:
          // any new league-doc field a client gate reads MUST be added here
          // or the gate sees undefined and fails open.
          pickPrivacy:      data.pickPrivacy      ?? null,
          isEnded, winner,
        };
      } catch { return null; }
    })
  );
  return leagues.filter(Boolean);
}

// ─── LOAD LEAGUE STANDINGS ───────────────────────────────────────────────────
// ─── LOAD LEAGUE STANDINGS ───────────────────────────────────────────────────
// scoringMode decides what "winning" means:
//   "wager"    → rank by point balance (the Vegas-odds ledger).
//   "pickem"   → rank by total correct picks. The member `wins` field IS the
//                score (1 pt per correct pick); balance is always 0 in these
//                leagues, so the old balance sort produced arbitrary Firestore
//                document order.
//   "survivor" → alive members first (by elimination week, latest = best), then
//                correct picks as the tiebreak among the still-alive.
//
// Defaults to "wager" so every existing league and every caller that doesn't
// pass a mode keeps the exact previous behaviour.
//
// `score` is a derived display field: the number the UI should show next to a
// member. Renders read this instead of branching on mode themselves.
export async function loadLeagueStandings(leagueId, scoringMode = "wager") {
  const membersSnap = await getDocs(collection(db, "leagues", leagueId, "members"));

  const isPickem   = scoringMode === "pickem";
  const isSurvivor = scoringMode === "survivor";
  const isNonWager = isPickem || isSurvivor;

  const statsArr = membersSnap.docs.map(d => {
    const data = d.data();
    return {
      id:       d.id,
      username: data.username || "Anonymous",
      balance:  data.balance  ?? 0,
      wins:     data.wins     || 0,
      losses:   data.losses   || 0,
      streak:   data.streak   || 0,
      lastPayday: data.lastPayday || null,
      // Survivor state. Absent = alive (nobody is eliminated until the survivor
      // settlement path ships and writes these).
      survivorStatus: data.survivorStatus || "alive",
      eliminatedWeek: data.eliminatedWeek ?? null,
      // Derived: what the standings row should display as this member's score.
      score: isNonWager ? (data.wins || 0) : (data.balance ?? 0),
    };
  });

  const sorted = statsArr.sort((a, b) => {
    if (isSurvivor) {
      // Alive beats eliminated, always.
      const aAlive = a.survivorStatus !== "eliminated";
      const bAlive = b.survivorStatus !== "eliminated";
      if (aAlive !== bAlive) return aAlive ? -1 : 1;
      // Among the eliminated, surviving longer ranks higher.
      if (!aAlive && !bAlive) {
        const aw = a.eliminatedWeek ?? 0;
        const bw = b.eliminatedWeek ?? 0;
        if (aw !== bw) return bw - aw;
      }
      // Among the alive (or same elimination week), more correct picks ranks higher.
      if (b.wins !== a.wins) return b.wins - a.wins;
      return a.losses - b.losses;
    }
    if (isPickem) {
      // Total correct picks. Fewer losses breaks a tie (a member who picked
      // fewer games shouldn't outrank one who picked more and matched them).
      if (b.wins !== a.wins) return b.wins - a.wins;
      return a.losses - b.losses;
    }
    return b.balance - a.balance;
  });

  return sorted.map((p, i) => ({ ...p, rank: i + 1 }));
}

// ─── DEDUCT WAGER FROM LEAGUE MEMBER BALANCE ─────────────────────────────────
// L-1 FIX: This is the client-side fallback debit (used only when the secure
// Cloud Function submit path is unavailable). It previously used a blind
// increment(-totalWager) with no read, no sufficiency check, and no floor,
// which -- exactly like the server bug -- let concurrent writes push a league
// member balance negative and bank it there. Now it re-reads the balance inside
// a transaction, refuses to debit below zero, and writes an explicit clamped
// value. Returns { ok, balance } so callers can detect a rejected debit.
export async function deductLeagueWager(leagueId, userId, totalWager) {
  if (!leagueId || !userId || !totalWager) return { ok: false, balance: null };
  const ref = doc(db, "leagues", leagueId, "members", userId);
  try {
    let finalBalance = null;
    await runTransaction(db, async (txn) => {
      const snap = await txn.get(ref);
      const cur = snap.data()?.balance ?? 0;
      if (cur < totalWager) {
        // Insufficient at commit time -- do not debit. Surfacing this as a
        // thrown error lets the caller avoid leaving an orphaned pick with no
        // matching debit; the caller already re-checks balance before writing.
        throw new Error(`Insufficient league balance (${cur} pts)`);
      }
      finalBalance = cur - totalWager;
      txn.update(ref, { balance: finalBalance });
    });
    return { ok: true, balance: finalBalance };
  } catch (e) {
    console.warn(`[Leagues] deductLeagueWager failed for ${userId} in ${leagueId}:`, e.message);
    return { ok: false, balance: null };
  }
}

// ─── UPDATE LEAGUE MEMBER BALANCE ────────────────────────────────────────────
// `count` is how many picks of this result are being applied in one call.
// Callers group a settle run's picks by league and pass the per-result count
// (e.g. 3 wins → count=3) alongside the summed balance `delta`. Wins, losses,
// and streak must move by `count`, not by 1 — otherwise a multi-win slate
// credits the balance correctly but records only a single win and advances the
// streak by one, silently under-counting the member's record and streak.
//
// Statuses that carry no W/L meaning ("push", "cashout") skip the win/loss
// branches entirely and only apply the balance delta — count is irrelevant
// for them but harmless to pass.
export async function updateLeagueMemberBalance(leagueId, userId, delta, resultStatus, count = 1) {
  const ref = doc(db, "leagues", leagueId, "members", userId);
  const update = {};

  // Guard: count must be a positive integer. Anything else (undefined from an
  // old 4-arg caller, 0, negative, NaN) collapses to 1 so legacy call sites
  // behave exactly as before.
  const n = Number.isFinite(count) && count >= 1 ? Math.floor(count) : 1;

  // Use increment() for balance — atomic, no read needed, no race conditions
  if (delta !== 0) update.balance = increment(delta);

  if (resultStatus === "won") {
    update.wins   = increment(n);
    update.streak = increment(n);
    // bestStreak needs a read since we're comparing — do it async separately.
    // The batch of `n` wins pushes the streak up by n, so the candidate peak
    // is (current streak + n), not (current streak + 1).
    getDoc(ref).then(snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      const newStreak = (d.streak || 0) + n;
      if (newStreak > (d.bestStreak || 0)) {
        updateDoc(ref, { bestStreak: newStreak }).catch(() => {});
      }
    }).catch(() => {});
  }
  if (resultStatus === "lost") {
    update.losses = increment(n);
    update.streak = 0;
  }

  if (Object.keys(update).length > 0) {
    await updateDoc(ref, update);
  }
}

// ─── GET LEAGUE MEMBER BALANCE ───────────────────────────────────────────────
export async function getLeagueMemberBalance(leagueId, userId) {
  try {
    const snap = await getDoc(doc(db, "leagues", leagueId, "members", userId));
    return snap.data()?.balance ?? 0;
  } catch { return 0; }
}

// ─── LOAD MY LEAGUE BALANCES (for PickSlipDrawer) ────────────────────────────
export async function loadMyLeagueBalances(userId) {
  if (!userId) return [];
  const userSnap  = await getDoc(doc(db, "users", userId));
  const leagueIds = userSnap.data()?.leagueIds || [];

  const results = await Promise.all(
    leagueIds.map(async (leagueId) => {
      try {
        const [lSnap, mSnap] = await Promise.all([
          getDoc(doc(db, "leagues", leagueId)),
          getDoc(doc(db, "leagues", leagueId, "members", userId)),
        ]);
        if (!lSnap.exists()) return null;
        const lData = lSnap.data();
        const mData = mSnap.data() || {};
        // Skip ended leagues — can't submit picks to them
        if (lData.status === "ended") return null;
        if (lData.endDate) {
          const end = lData.endDate.toDate ? lData.endDate.toDate() : new Date(lData.endDate);
          if (end < new Date()) return null;
        }
        return {
          id:      leagueId,
          name:    lData.name,
          balance: mData.balance ?? lData.startingBalance ?? DEFAULT_BALANCE,
        };
      } catch { return null; }
    })
  );
  return results.filter(Boolean);
}

// ─── LEAVE LEAGUE ────────────────────────────────────────────────────────────
export async function leaveLeague(userId, leagueId) {
  // SERVER-OWNED-BALANCES (fix 4.1 step 2): leaveLeagueCallable refuses while a
  // pick in this league is pending and saves the season record (fix 3.4).
  try {
    await httpsCallable(fns, "leaveLeagueCallable")({ leagueId });
  } catch (e) {
    const err = new Error(e?.message && !/^internal$/i.test(e.message) ? e.message : "Couldn't leave. Try again.");
    if (e?.details?.reason === "pending-picks") err.code = "torch/pending-picks";
    throw err;
  }
}

// ─── STORE LEAGUE ID ON USER DOC ─────────────────────────────────────────────
export async function addLeagueToUser(userId, leagueId) {
  try {
    await setDoc(doc(db, "users", userId), { leagueIds: arrayUnion(leagueId) }, { merge: true });
    console.log("[addLeagueToUser] OK — leagueIds updated for", userId);
  } catch (e) {
    console.error("[addLeagueToUser] FAILED", e.code, e.message);
    // Try updateDoc as fallback
    try {
      await updateDoc(doc(db, "users", userId), { leagueIds: arrayUnion(leagueId) });
      console.log("[addLeagueToUser] Fallback updateDoc OK");
    } catch (e2) {
      console.error("[addLeagueToUser] Fallback also failed", e2.code, e2.message);
      throw new Error("Could not update leagueIds (" + e2.code + "): " + e2.message);
    }
  }
}

// ─── DELETE LEAGUE (commissioner only) ───────────────────────────────────────
export async function deleteLeague(leagueId, userId, memberIds = []) {
  const leagueRef  = doc(db, "leagues", leagueId);
  const leagueSnap = await getDoc(leagueRef);

  if (!leagueSnap.exists()) throw new Error("League not found.");

  const data   = leagueSnap.data();
  const commish = data.commissionerId ?? data.commissionerUid ?? data.createdBy ?? null;

  if (commish && commish !== userId) {
    throw new Error("Only the league commissioner can delete this league.");
  }

  const batch = writeBatch(db);

  const SUBCOLLECTIONS = ["members", "standings", "activity"];
  for (const sub of SUBCOLLECTIONS) {
    const subSnap = await getDocs(collection(db, "leagues", leagueId, sub));
    subSnap.docs.forEach(d => batch.delete(d.ref));
  }

  try {
    const userRef  = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const ids = (userSnap.data()?.leagueIds || []).filter(id => id !== leagueId);
      batch.update(userRef, { leagueIds: ids });
    }
  } catch { /* non-fatal */ }

  batch.delete(leagueRef);

  await batch.commit();
}

// ─── WRITE ACTIVITY EVENT ─────────────────────────────────────────────────────
export async function writeLeagueActivity(userId, leagueIds, event) {
  if (!leagueIds?.length) return;
  const payload = {
    ...event,
    userId,
    username:  event.username || "Anonymous",
    type:      event.type,
    label:     event.label,
    sport:     event.sport,
    points:    event.points ?? 0,
    wager:     event.wager  ?? 0,
    createdAt: serverTimestamp(),
  };
  await Promise.all(
    leagueIds.map(leagueId =>
      addDoc(collection(db, "leagues", leagueId, "activity"), payload).catch(() => {})
    )
  );
}

// ─── LOAD LEAGUE ACTIVITY FEED ───────────────────────────────────────────────
export async function loadLeagueActivity(leagueId, count = 30) {
  try {
    const q = query(
      collection(db, "leagues", leagueId, "activity"),
      orderBy("createdAt", "desc"),
      limit(count)
    );
    const snap = await getDocs(q);
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    items.sort((a, b) => {
      const ta = a.createdAt?.seconds ?? a.createdAt?.toMillis?.() ?? 0;
      const tb = b.createdAt?.seconds ?? b.createdAt?.toMillis?.() ?? 0;
      return tb - ta;
    });
    return items;
  } catch (e) {
    console.error("[Leagues] loadLeagueActivity failed:", e.message);
    return [];
  }
}

// ─── LOAD PUBLIC LEAGUES ─────────────────────────────────────────────────────
export async function loadPublicLeagues(userId, count = 20) {
  try {
    const q = query(
      collection(db, "leagues"),
      where("isPublic", "==", true),
      orderBy("memberCount", "desc"),
      limit(count)
    );
    const snap = await getDocs(q);
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(l => {
        if (l.endDate) {
          const end = l.endDate.toDate ? l.endDate.toDate() : new Date(l.endDate);
          if (end < new Date()) return false;
        }
        return true;
      });
  } catch (e) {
    console.error("[Leagues] loadPublicLeagues failed:", e.message);
    return [];
  }
}

// ─── LIVE SUBSCRIPTION TO MEMBER DOC ─────────────────────────────────────────
export function subscribeToMemberDoc(leagueId, userId, callback) {
  if (!leagueId || !userId) return () => {};
  const ref = doc(db, "leagues", leagueId, "members", userId);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) callback(snap.data());
  }, () => {});
}

// ─── UPDATE LEAGUE PAYDAY SETTINGS (commissioner only) ───────────────────────
export async function updateLeaguePaydaySettings(leagueId, userId, settings) {
  const leagueRef  = doc(db, "leagues", leagueId);
  const leagueSnap = await getDoc(leagueRef);
  if (!leagueSnap.exists()) throw new Error("League not found.");
  if (leagueSnap.data().commissionerId !== userId) {
    throw new Error("Only the commissioner can change payday settings.");
  }

  const { paydayEnabled, paydayAmount, paydayDay, paydayFrequency } = settings;

  if (typeof paydayAmount === "number" && (paydayAmount < 0 || paydayAmount > 10000)) {
    throw new Error("Payday amount must be between 0 and 10,000 pts.");
  }
  if (typeof paydayDay === "number" && (paydayDay < 0 || paydayDay > 6)) {
    throw new Error("Invalid payday day.");
  }

  await updateDoc(leagueRef, {
    ...(typeof paydayEnabled   !== "undefined" ? { paydayEnabled }   : {}),
    ...(typeof paydayAmount    !== "undefined" ? { paydayAmount }    : {}),
    ...(typeof paydayDay       !== "undefined" ? { paydayDay }       : {}),
    ...(typeof paydayFrequency !== "undefined" ? { paydayFrequency } : {}),
  });
}

// ─── RUN LEAGUE PAYDAYS ───────────────────────────────────────────────────────

// ─── END LEAGUE (crown champion, lock it) ────────────────────────────────────
// Called automatically by the Cloud Function when endDate passes (no actorUid —
// runs server-side via Admin SDK, which bypasses security rules), or manually by
// the commissioner from the client (actorUid = the commissioner's uid).
//
// The Den "season_end" activity event must be written with userId == the authed
// caller's uid to satisfy the activity-create rule (userId == request.auth.uid).
// Server-side calls bypass rules, so the legacy "system" attribution is fine there;
// client calls MUST pass actorUid or the activity write is rejected.
export async function endLeague(leagueId, actorUid = null) {
  const leagueRef = doc(db, "leagues", leagueId);
  const leagueSnap = await getDoc(leagueRef);
  if (!leagueSnap.exists()) throw new Error("League not found.");
  const data = leagueSnap.data();

  // Already ended? Skip.
  if (data.status === "ended") return null;

  // Get final standings — MODE-AWARE. Without passing scoringMode this
  // defaulted to the wager sort (balance desc); in a pickem/survivor league
  // every balance is 0, so the "champion" was whoever happened to sort first
  // in doc order instead of the member with the most correct picks.
  const lgMode = data.scoringMode || "wager";
  const lgNonWager = lgMode === "pickem" || lgMode === "survivor";
  const standings = await loadLeagueStandings(leagueId, lgMode);
  const champion = standings.length > 0 ? {
    uid:      standings[0].id,
    username: standings[0].username,
    balance:  standings[0].balance,
    wins:     standings[0].wins,
    losses:   standings[0].losses,
    rank:     1,
  } : null;

  // Update league doc
  await updateDoc(leagueRef, {
    status:   "ended",
    endedAt:  serverTimestamp(),
    champion: champion,
  });

  // Write Den activity event. Attribute to the acting commissioner when called
  // from the client (so the activity-create rule passes); fall back to "system"
  // for server-side (Admin SDK) calls where rules are bypassed.
  // Non-wager leagues lead with correct picks — "wins with 0 pts" reads broken.
  const champLabel = champion
    ? (lgNonWager
        ? `🏆 Season over! ${champion.username} wins with ${champion.wins} correct pick${champion.wins === 1 ? "" : "s"} (${champion.wins}-${champion.losses})!`
        : `🏆 Season over! ${champion.username} wins with ${champion.balance.toLocaleString()} pts and a ${champion.wins}-${champion.losses} record!`)
    : "Season has ended!";

  await addDoc(collection(db, "leagues", leagueId, "activity"), {
    userId:    actorUid || "system",
    username:  "Torch",
    type:      "season_end",
    label:     champLabel,
    sport:     "",
    points:    0,
    wager:     0,
    createdAt: serverTimestamp(),
  });

  console.info(`[endLeague] ${data.name} ended — champion: ${champion?.username || "none"}`);
  return champion;
}

// ─── RESTART LEAGUE (new season, keep members) ──────────────────────────────
// Commissioner-only. Resets all member balances and records to starting values.
// Archives the old season's champion data on the league doc.
export async function restartLeague(leagueId, userId, newEndDate = null) {
  // SERVER-SIDE since Aug 2026. The old client implementation batch-updated
  // every member doc from the commissioner's browser; the members security
  // rule is owner-only-update (deliberately -- a commissioner clause would
  // let commissioners rewrite members' balances from devtools), so the
  // atomic batch always failed with "Missing or insufficient permissions".
  // endLeague never hit this because it writes only the league doc.
  // restartLeagueCallable (functions/index.js) does the same reset with the
  // Admin SDK: commissioner check, status must be "ended", season archived,
  // members reset to startingBalance, endDate PRESERVED unless a new one is
  // passed (the old client version silently erased it).
  // `userId` is kept in the signature for call-site compatibility; the CF
  // authenticates from the callable context, not this argument.
  const callFn = httpsCallable(fns, "restartLeagueCallable");
  const result = await callFn({ leagueId, newEndDate });
  console.info(`[restartLeague] ${leagueId} restarted -- season ${result.data?.season}`);
  return { season: result.data?.season };
}

// ─── CHECK IF LEAGUE IS ENDED ───────────────────────────────────────────────
// Simple helper used by UI to check if picks should be blocked
export function isLeagueEnded(league) {
  if (league.status === "ended") return true;
  if (!league.endDate) return false;
  const end = league.endDate?.toDate ? league.endDate.toDate()
    : league.endDate?.seconds ? new Date(league.endDate.seconds * 1000)
    : new Date(league.endDate);
  return end < new Date();
}

// ─── RUN LEAGUE PAYDAYS ───────────────────────────────────────────────────────
//
// Called on login for every league the user is in.
// For each league, checks if a payday is due based on the commissioner's
// configured schedule, then atomically credits ALL members' league balances.
//
// Payday tracking is per-league (lastLeaguePayday on the league doc) so all
// members get paid together — no one gets paid twice, no one gets skipped.
//
// Returns: array of { leagueId, leagueName, amount } for leagues that paid out
//
export async function runLeaguePaydays(userId, leagues = []) {
  // SERVER-OWNED-BALANCES (fix 4.1 step 2, 2026-09-28): league paydays are paid
  // only by the server now. runScheduledPaydays runs hourly and, since fix
  // 4.1a, catches up anyone behind on ANY day, so this app-side catch-up (and
  // its balance write) is retired. Kept as a no-op so callers don't change.
  return [];
}
